import type { FC } from 'react';

import { useCms, useContent, useSetting } from '@eneco/cms';
import { Box, Card, Grid, Heading, IconLoader, Stretch, TextLink } from '@eneco/sparky';
import { ChevronRightIcon } from '@eneco/sparky/icons';
import type { HeadingProps } from '@eneco/sparky/types';

import type { LinkCardContent } from './link-card-content.types';

/**
 * `LinkCard` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy Sitecore lane (`libs/sitecore/components/content/src/LinkCard/LinkCard.tsx`): an
 * optional title, then a grid of links (laid out into `columns` columns, default 1) each rendered as a
 * `TextLink` with a leading icon, falling back to a chevron. Drops the legacy `wrap()`/Experience Editor
 * `editable` checks — this lane never runs inside the Experience Editor — and the legacy
 * `useLinkComponent()` in favor of `adapter.toLink()` plus a plain `TextLink`, matching every other
 * migrated component in this lane. `columns`/`headingLevel` are Sitecore rendering parameters, read via
 * `useSetting` like every other migrated block's appearance settings.
 *
 * Known simplification: the migration bundle's `LinkCard` schema
 * (`libs/cms-sanity-schema/src/sections/LinkCard.ts`) has no `items` field at all — the link list, this
 * component's entire content payload, was not carried over by the schema generator, and neither were
 * `columns`/`headingLevel`. `items` therefore only ever populates on the Sitecore lane; the Sanity lane
 * renders the title only (if present), in a single column. See `link-card-content.types.ts`.
 *
 * Known simplification: the legacy `icon` droplink resolves to a real icon name only through Sitecore's
 * own field-unwrap path (see `iconName` below); the migration bundle's `icon` is an unresolved
 * `migrationUnresolvedReference` (ADR-0049) with no name to read, so the Sanity lane always falls back
 * to the default chevron icon.
 */
const LinkCard: FC = () => {
  const { adapter } = useCms();
  const content = useContent<LinkCardContent>();

  const columns = useSetting('columns') ?? '1';
  const headingLevel = (useSetting('headingLevel') as HeadingProps['as']) ?? 'h2';

  const items = (content.items ?? [])
    .map(item => ({ id: item.id, link: adapter.toLink(item.link) }))
    .filter(item => item.link?.href);

  if (items.length === 0 && !content.title) return null;

  const rows = Math.ceil(items.length / Number.parseInt(columns, 10));

  return (
    <Stretch height>
      <Card>
        <Box padding="8">
          {content.title && (
            <Box paddingBottom="6">
              <Heading size="S" as={headingLevel}>
                {content.title}
              </Heading>
            </Box>
          )}
          <Grid gridTemplateRows={{ md: `repeat(${rows}, 1fr)` }} flow={{ md: 'column' }} gap="5" as="ul">
            {items.map(item => (
              <Grid.Item key={item.id} as="li">
                <TextLink
                  href={item.link?.href}
                  emphasis="medium"
                  leftIcon={<IconLoader iconName={iconName(content)} fallbackIcon={<ChevronRightIcon />} />}
                  target={item.link?.target}>
                  {item.link?.text}
                </TextLink>
              </Grid.Item>
            ))}
          </Grid>
        </Box>
      </Card>
    </Stretch>
  );
};

/**
 * Sitecore lane only, and best-effort: traces `useContent()`'s unwrap of the legacy droplink
 * (`libs/sitecore/types/manual/LinkCard.ts`'s `icon.fields.icon.name`) through
 * `libs/cms-sitecore/src/mappers.ts`'s `unwrap`/`isItem`, which lifts the droplink item's `fields` up a
 * level and leaves its nested `icon` field object (`{ name }`) untouched alongside it. See
 * `link-card-content.types.ts` for why this can't be typed from a generated source.
 */
const iconName = (content: LinkCardContent): string | undefined => {
  const icon = content.icon as { icon?: { name?: unknown } } | undefined;
  const name = icon?.icon?.name;
  return typeof name === 'string' ? name : undefined;
};

export default LinkCard;
